/* global process */
import constants from '../../Constants';
import xmlDomUtils from '../../utils/XmlDomUtils';
import { isInteraction } from './itemBody';
import environmentNames from '../../environmentNames';
import { parsePDFModel } from './PDFPanelModel';
import utilService from '../../../services/UtilService';
import mediaSupportService from '../../../services/MediaSupportService';
import settingsReader from '../../../store/readHelpers/SettingsReader';
import marketplacePropertiesService from '../../../services/MarketplacePropertiesService';
import multimediaCanPauseModes from '../../../../src/components/question/media/multimedia/MultimediaDisplayConstants';

function getObjectTypeValue(element) {
    // document stores objectType as objecttype
    if (element.hasAttribute(constants.contentAttributes.OBJECT_TYPE.toLowerCase())) {
        return element.getAttribute(constants.contentAttributes.OBJECT_TYPE.toLowerCase());
    } else if (element.hasAttribute(constants.contentAttributes.OBJECT_TYPE)) {
        return element.getAttribute(constants.contentAttributes.OBJECT_TYPE);
    }
    return undefined;
}

function getLogicalNameValue(element) {
    // the attribute can be logicalName or logicalname
    if (element.hasAttribute(constants.contentAttributes.LOGICAL_NAME.toLowerCase())) {
        return element.getAttribute(constants.contentAttributes.LOGICAL_NAME.toLowerCase());
    } else if (element.hasAttribute(constants.contentAttributes.LOGICAL_NAME)) {
        return element.getAttribute(constants.contentAttributes.LOGICAL_NAME);
    }
    return undefined;
}

function objectTypeHasValue(element, value) {
    return (getObjectTypeValue(element) === value);
}

function isOldFormatImage(element) {
    return element.nodeType === 1
        && element.hasAttribute(constants.contentAttributes.LOGICAL_NAME)
        && objectTypeHasValue(element, 'content_image');
}

function getMultimediaMimeType(element) {
    const mimeType = element.getAttribute('type') || element.getAttribute('inspera:mimeType');
    if (mimeType) {
        return mimeType;
    }
    // reading it from the first source tag
    const sourceElement = Array
        .from(element.childNodes)
        .find(node => node.nodeType === 1 && node.tagName === 'source');

    if (sourceElement) {
        return sourceElement.getAttribute('type');
    }
    return mimeType;
}

export function isMainMediaElement(element) {
    const parent = element.parentNode;
    return parent && parent.nodeType === 1 &&
        (parent.getAttribute('id') === 'illustration' || parent.getAttribute('class') === 'question-main-illustration');
}

export function isNewFormatImage(element) {
    return element.nodeType === 1
        && element.localName === 'img'
        && element.hasAttribute(constants.contentAttributes.LOGICAL_NAME_DATA);
}

function getImageLogicalName(element) {
    if (isOldFormatImage(element)) {
        return element.getAttribute(constants.contentAttributes.LOGICAL_NAME);
    } else if (isNewFormatImage(element)) {
        return element.getAttribute(constants.contentAttributes.LOGICAL_NAME_DATA);
    }

    return undefined;
}


export function generateFileUrlForLogicalName(logicalName, mimeType = '', assessmentRunId, type = 'image', format) {
    const offlineMode = window.offlineMode;
    let url = '';
    const typeMap = { image: 'img' };
    const typePart = typeMap[type] || type;

    if (process.env.NODE_ENV === environmentNames.DEVELOPMENT || offlineMode) {
        let extension = '';
        if (mimeType.indexOf('audio') === 0) {
            extension = '.mp3';
        } else if (mimeType.indexOf('video') === 0) {
            extension = '.mp4';
        }

        url = `${offlineMode ? 'testData/files' : '/files'}/${logicalName}${extension}`;
    } else {
        url = `/file/cil/${logicalName}/${typePart}?cacheable=true`;
        if (assessmentRunId) {
            url += `&assessmentRunId=${assessmentRunId}`;
        }
        if (format) {
            url += `&format=${format}`;
        }
    }

    return url;
}

export function isFileLink(element) {
    return element.nodeType === 1 && element.localName === 'a' && element.hasAttribute(constants.contentAttributes.LOGICAL_NAME);
}

export function isImage(element) {
    return isNewFormatImage(element) || isOldFormatImage(element);
}

export function isAudio(element) {
    return element.nodeType === 1
        && element.hasAttribute(constants.contentAttributes.LOGICAL_NAME)
        && objectTypeHasValue(element, 'content_sound');
}

export function isVideo(element) {
    return element.nodeType === 1
        && element.hasAttribute(constants.contentAttributes.LOGICAL_NAME)
        && objectTypeHasValue(element, 'content_video_h264');
}

export function isPdf(element) {
    return element.nodeType === 1
        && element.hasAttribute(constants.contentAttributes.LOGICAL_NAME)
        && objectTypeHasValue(element, 'content_url')
        && element.getAttribute('type') === 'application/pdf';
}

export function isEmbed(element) {
    if (element.nodeType !== 1 || element.localName !== 'object') {
        return false;
    }

    const hasEmbedCodeClass = element.getAttribute('class') === 'embedCode';
    const iframeList = element.getElementsByTagName('iframe');

    return hasEmbedCodeClass && iframeList && iframeList.length === 1;
}

/**
 * Checks if a DOM Element is a media element that should be a media model.
 *
 * @param {Element} element
 * @returns {boolean}
 */
export function isMedia(element) {
    return element.nodeType === 1
        && (isImage(element) || isAudio(element) || isEmbed(element) || isFileLink(element) || isVideo(element));
}

export function isInsideInteraction(element) {
    if (!element || !element.parentNode) {
        return false;
    }

    return isInteraction(element.parentNode) || (element.parentNode && isInsideInteraction(element.parentNode));
}

export function getMediaTargetId(questionIdentifier, element) {
    if (isImage(element)) {
        return `${questionIdentifier}_${getImageLogicalName(element)}`;
    } else if (isEmbed(element)) {
        return `${questionIdentifier}_${element.getAttribute('class')}`;
    } else if (isAudio(element) || isVideo(element)) {
        return `${questionIdentifier}_${element.getAttribute(constants.contentAttributes.LOGICAL_NAME)}`;
    } else if (isFileLink(element)) {
        return `${questionIdentifier}_${element.getAttribute(constants.contentAttributes.LOGICAL_NAME)}`;
    }

    return undefined;
}

export function getMediaPlaceholderType(element) {
    if (isFileLink(element) || (isNewFormatImage(element) && !isMainMediaElement(element))) {
        return 'span';
    }

    return 'div';
}

export function parseFileLink(questionIdentifier, linkElement, assessmentRunId, mediaInfo) {
    const type = constants.mediaType.LINK;
    const objectType = getObjectTypeValue(linkElement);
    const logicalName = linkElement.getAttribute(constants.contentAttributes.LOGICAL_NAME);
    let url = generateFileUrlForLogicalName(
        logicalName,
        undefined,
        assessmentRunId,
        constants.mediaType.LINK,
    );
    let alternativeUrl;
    const linkContent = linkElement.textContent;
    const linkTarget = '_blank';
    const target = getMediaTargetId(questionIdentifier, linkElement);
    const mediaInfoItem = mediaInfo?.find(item => item.logicalName === logicalName);
    if (mediaInfoItem?.presignedURL && settingsReader.shouldUsePresignedUrl()) {
        alternativeUrl = url;
        url = mediaInfoItem.presignedURL;
    }
    return {
        type,
        objectType,
        url,
        linkContent,
        linkTarget,
        target,
        alternativeUrl,
    };
}

export function getIframeAttributes(iframeElement) {
    const attributesFromIframeNode = Array.from(iframeElement.attributes);
    const extraAttributes = {};
    attributesFromIframeNode.forEach((attribute) => {
        if (constants.allowedIframeAttributes.includes(attribute.nodeName)) {
            extraAttributes[attribute.nodeName] = attribute.nodeValue ? attribute.nodeValue : '';
        } else if (attribute.nodeName === 'allowfullscreen') {
            extraAttributes.allowFullScreen = true;
        }
    });
    return extraAttributes;
}

export function parseEmbed(questionIdentifier, embedElement) {
    const isMainMedia = isMainMediaElement(embedElement);
    const type = constants.mediaType.EMBED;
    const objectType = getObjectTypeValue(embedElement);
    const iframeElement = embedElement.getElementsByTagName('iframe')[0];
    const url = iframeElement.getAttribute('src');
    const width = parseInt(iframeElement.getAttribute('width'), 10);
    const height = parseInt(iframeElement.getAttribute('height'), 10);
    const target = getMediaTargetId(questionIdentifier, embedElement);
    const extraAttributes = getIframeAttributes(iframeElement);
    return {
        type,
        objectType,
        isMainMedia,
        url,
        width,
        height,
        target,
        extraAttributes,
    };
}

export function getStylesObjectFromString(styleString) {
    const styles = styleString
        .split(';')
        .reduce((accumulator, style) => {
            const [key, value] = style.split(':').map(part => part.trim());
            accumulator[key] = value;
            return accumulator;
        }, {});
    return styles;
}

export function getStyleNamesFromString(stylesString) {
    return stylesString.trim().split(/\s+/);
}

/**
 * Model for an image
 * @param questionIdentifier
 * @param imageElement
 * @param assessmentRunId - optional
 * @param mediaInfo - optional
 * @returns {{type: string, isMainMedia: boolean, logicalName: string, url, orgWidth: Number, orgHeight: Number, target: string}}
 */
export function parseImage(questionIdentifier, imageElement, assessmentRunId, mediaInfo) {
    const orgModel = xmlDomUtils.getAttributes(imageElement);
    const imageModel = {};
    imageModel.isMainMedia = isMainMediaElement(imageElement);
    imageModel.logicalName = getImageLogicalName(imageElement);
    imageModel.mimeType = orgModel.type;
    imageModel.type = constants.mediaType.IMAGE;
    imageModel.objectType = getObjectTypeValue(imageElement);
    imageModel.target = getMediaTargetId(questionIdentifier, imageElement);
    imageModel.id = imageModel.logicalName;
    if (orgModel.style) {
        imageModel.originalStyle = getStylesObjectFromString(orgModel.style);
    }
    if (orgModel.class) {
        imageModel.originalClassNames = getStyleNamesFromString(orgModel.class);
    }

    const mediaInfoItem = mediaInfo?.find(item => item.logicalName === imageModel.logicalName);
    const fileServletUrl = generateFileUrlForLogicalName(imageModel.logicalName, imageModel.mimeType, assessmentRunId, imageModel.type);
    if (mediaInfoItem?.presignedURL && settingsReader.shouldUsePresignedUrl()) {
        imageModel.url = mediaInfoItem.presignedURL;
        imageModel.alternativeUrl = fileServletUrl;
    }
    if (imageModel.url === undefined) {
        imageModel.url = fileServletUrl;
    }

    if (orgModel['data-align']) {
        imageModel.align = orgModel['data-align'];
    }

    if ((orgModel['inspera:orgWidth'] || orgModel['data-orgwidth']) && !orgModel.width) {
        imageModel.orgWidth = parseInt(orgModel['inspera:orgWidth'], 10) || parseInt(orgModel['data-orgwidth'], 10);
        imageModel.orgHeight = parseInt(orgModel['inspera:orgHeight'], 10) || parseInt(orgModel['data-orgheight'], 10);
    } else if (orgModel.orgWidth) {
        imageModel.orgWidth = parseInt(orgModel.orgWidth, 10);
        imageModel.orgHeight = parseInt(orgModel.orgHeight, 10);
    }

    if (orgModel.width) {
        imageModel.width = parseInt(orgModel.width, 10);
        imageModel.height = parseInt(orgModel.height, 10);
    }

    if (orgModel.x) {
        imageModel.x = parseInt(orgModel.x, 10);
        imageModel.y = parseInt(orgModel.y, 10);
    }

    if (orgModel.top) {
        imageModel.top = parseInt(orgModel.top, 10);
        imageModel.left = parseInt(orgModel.left, 10);
    }

    if (imageModel.isMainMedia) {
        imageModel.maxHeight = '';
        if (orgModel['inspera:maxHeight'] === 'none' || orgModel['data-maxheight'] === 'none') {
            imageModel.maxHeight = 'none';
        } else {
            // Use 'recommended' if maxHeight is not defined.
            imageModel.maxHeight = 'recommended';
        }
    }

    imageModel.altText = '';
    if (orgModel['inspera:alternativeText']) {
        imageModel.altText = orgModel['inspera:alternativeText'];
    } else if (orgModel.alt) {
        imageModel.altText = orgModel.alt;
    }

    return imageModel;
}

/**
 * Detects number of allowed repeat count.
 * @param node
 * @returns {int} number of repeat counts as following:
 * -1 = unlimited repetitions
 *  0 = no repetitions
 *  1..n = 1 or more repetitions
 */
export function detectMediaRepeatTimes(node) {
    let repeatTimes;
    const nodeAttributes = xmlDomUtils.getAttributes(node);

    if (xmlDomUtils.hasClass(node, 'canRepeat') || !nodeAttributes.class) {
        repeatTimes = -1; // unlimited repeat counts
    } else {
        repeatTimes = nodeAttributes.class.match(/canRepeat_(-?\d+)/);
        if (Array.isArray(repeatTimes) && repeatTimes.length > 1) {
            repeatTimes = parseInt(repeatTimes[1], 10);
        } else {
            repeatTimes = 0; // cannot repeat media
        }
    }
    return repeatTimes;
}

/**
 * Model for video and audio clips
 * @param questionIdentifier
 * @param multimediaElement
 * @param assessmentRunId - optional
 * @param mediaInfo - optional defining formats available from transcoding for the media file
 * @returns {{logicalName: string, url, target: string, mimeType: string, type: 'audio', id: 'string'}}
 */
export function parseMultimedia(questionIdentifier, multimediaElement, assessmentRunId, mediaInfo) {
    const orgModel = xmlDomUtils.getAttributes(multimediaElement);
    const parentElementAttributes = xmlDomUtils.getAttributes(multimediaElement.parentNode);
    const model = {};

    model.id = parentElementAttributes.id;
    model.interactionVisibility = orgModel['inspera:interactionVisibility'];
    model.logicalName = getLogicalNameValue(multimediaElement);
    model.mimeType = getMultimediaMimeType(multimediaElement);
    model.objectClass = orgModel.class;
    model.objectType = getObjectTypeValue(multimediaElement);
    model.prompt = orgModel['data-label'];
    model.questionId = questionIdentifier;
    model.repeatTimes = detectMediaRepeatTimes(multimediaElement);
    model.skin = orgModel['inspera:mediaPlayerSkin'] || 'default';
    model.target = getMediaTargetId(questionIdentifier, multimediaElement);
    model.sources = [];
    model.canPauseMode = null;

    if (isVideo(multimediaElement)) {
        model.type = constants.mediaType.VIDEO;
        model.height = utilService.getIntOr(orgModel.height);
        model.width = utilService.getIntOr(orgModel.width);
    } else {
        model.type = constants.mediaType.AUDIO;
    }
    if (marketplacePropertiesService.isEnableMediaPauseModeOn()) {
        setEnablePauseWithMppOn(orgModel, model, multimediaElement);
    } else {
        setEnablePauseWithMppOff(orgModel, model, multimediaElement);
    }

    const mediaInfoItem = mediaInfo?.find(item => item.logicalName === model.logicalName);
    const baseServletUrl = generateFileUrlForLogicalName(model.logicalName, model.mimeType, assessmentRunId, model.type);

    // Add format specific sources if available.
    if (mediaInfoItem?.formatsInfo?.length > 0) {
        const browserSupportedFormats = isVideo(multimediaElement)
            ? mediaSupportService.getSupportedVideoFormats()
            : mediaSupportService.getSupportedAudioFormats();

        model.sources = browserSupportedFormats
            .map((formatModel) => {
                const { presignedURL } = mediaInfoItem.formatsInfo.find(formatsInfoItem => formatModel.format === formatsInfoItem.format);
                return { ...formatModel, presignedURL };
            })
            .filter(formatModel => formatModel.presignedURL)
            .reduce((accumulator, { format, presignedURL, mimeType }) => {
                if (presignedURL && settingsReader.shouldUsePresignedUrl()) {
                    accumulator.push({ url: presignedURL, mimeType });
                }
                accumulator.push({ url: `${baseServletUrl}&format=${format}`, mimeType });
                return accumulator;
            }, []);
    }

    // Add default file servlet url, and corresponding pre signed url if available.
    if (mediaInfoItem?.presignedURL && settingsReader.shouldUsePresignedUrl()) {
        model.sources.push({ url: mediaInfoItem.presignedURL });
    }
    model.sources.push({ url: baseServletUrl });

    return model;
}

export function setEnablePauseWithMppOn(orgModel, model, multimediaElement) {
    if (isAudio(multimediaElement) && orgModel['inspera:enablePause'] === undefined) {
        const audioPauseMode = multimediaElement.hasAttribute('class') ?
            xmlDomUtils.hasClass(multimediaElement, 'enablePause') : true;
        model.canPauseMode = audioPauseMode ? 'basic' : 'strict';
    } else if (orgModel['inspera:enablePause'] === 'false') {
        model.canPauseMode = multimediaCanPauseModes.STRICT;
    } else if (
        orgModel['inspera:enablePause'] === 'true' ||
        orgModel['inspera:enablePause'] === '' ||
        (orgModel['inspera:enablePause'] === undefined &&
        orgModel['inspera:enablepause'] === undefined) ||
        orgModel['inspera:enablePause'] === null
    ) {
        model.canPauseMode = multimediaCanPauseModes.BASIC;
    } else {
        model.canPauseMode = orgModel['inspera:enablePause'];
    }
}

export function setEnablePauseWithMppOff(orgModel, model, multimediaElement) {
    if (isAudio(multimediaElement) && orgModel['inspera:enablePause'] === undefined) {
        model.canPause = multimediaElement.hasAttribute('class') ?
            xmlDomUtils.hasClass(multimediaElement, 'enablePause') : true;
    } else if (orgModel['inspera:enablePause'] === multimediaCanPauseModes.BASIC) {
        model.canPause = true;
    } else if (orgModel['inspera:enablePause'] === multimediaCanPauseModes.STRICT ||
        orgModel['inspera:enablePause'] === multimediaCanPauseModes.MODERATE) {
        model.canPause = false;
    } else {
        model.canPause = orgModel['inspera:enablePause'] !== 'false' && orgModel['inspera:enablepause'] !== 'false';
    }
}

export function parseMedia(questionIdentifier, mediaElement, assessmentRunId, mediaInfo) {
    // interactions with media handle it themselves (by using the media components directly)
    if (isInsideInteraction(mediaElement)) {
        return undefined;
    } else if (isImage(mediaElement)) {
        return parseImage(questionIdentifier, mediaElement, assessmentRunId, mediaInfo);
    } else if (isAudio(mediaElement) || isVideo(mediaElement)) {
        return parseMultimedia(questionIdentifier, mediaElement, assessmentRunId, mediaInfo);
    } else if (isEmbed(mediaElement)) {
        return parseEmbed(questionIdentifier, mediaElement);
    } else if (isFileLink(mediaElement)) {
        return parseFileLink(questionIdentifier, mediaElement, assessmentRunId, mediaInfo);
    } else if (isPdf(mediaElement)) {
        const isMainMedia = isMainMediaElement(mediaElement);
        return parsePDFModel(mediaElement, assessmentRunId, questionIdentifier, isMainMedia, mediaInfo);
    }

    return undefined;
}

export function getNearestParentLangAttribute(element) {
    let lang;
    let parentNode = element.parentNode;

    while (!lang && parentNode && parentNode.nodeType === Node.ELEMENT_NODE) {
        lang = parentNode.getAttribute('lang');
        parentNode = parentNode.parentNode;
    }

    return lang || undefined;
}

/**
 * @param {number|string} questionIdentifier
 * @param {Element} xmlElement
 * @param {number} assessmentRunId
 * @param {string} language
 * @param {object} mediaInfo
 * @param {string} itemBodyDefaultLanguage
 * @param {string} itemBodySupportedLanguages
 * @return {Array} Array of media models
 */
export function getMediaModels(questionIdentifier, xmlElement, assessmentRunId,
    language, mediaInfo, itemBodyDefaultLanguage, itemBodySupportedLanguages) {
    const defaultLanguage = xmlElement.getAttribute(constants.defaultLanguageAttr) || itemBodyDefaultLanguage;
    const supportedLanguages = xmlElement.getAttribute(constants.supportedLanguagesAttr) || itemBodySupportedLanguages;
    const languageIsSupported = typeof supportedLanguages === 'string'
        ? supportedLanguages.includes(language)
        : false;

    const langToUse = languageIsSupported ? language : defaultLanguage;
    const elements = xmlElement.getElementsByTagName('*');
    const mediaModels = [];

    for (const element of Array.from(elements)) {
        if (isMedia(element)) {
            let model;
            if (isAudio(element) || isVideo(element) || isImage(element)) {
                // audio and video media has language support
                const mediaObjLang = element.parentNode.getAttribute('lang');
                if (mediaObjLang === langToUse) {
                    model = parseMedia(questionIdentifier, element, assessmentRunId, mediaInfo);
                }
                if (!mediaObjLang && langToUse === defaultLanguage) {
                    model = parseMedia(questionIdentifier, element, assessmentRunId, mediaInfo);
                } else {
                    // media without language support, but has to be shown for all languages
                    // (i.e. background image in GTE, mail illustration)
                    const languageAgnosticClasses = ['iainteractive', 'block-interaction', 'question-main-illustration', 'insertedImage'];
                    const parentElemClasses = element.parentNode.getAttribute('class');
                    const elemClasses = element.getAttribute('class');
                    if (
                        !mediaObjLang &&
                        (languageAgnosticClasses.some(agnosticClass => parentElemClasses.includes(agnosticClass)) ||
                            languageAgnosticClasses.some(agnosticClass => elemClasses.includes(agnosticClass)))
                    ) {
                        model = parseMedia(questionIdentifier, element, assessmentRunId, mediaInfo);
                    }
                }
            } else if (isFileLink(element)) {
                const mediaObjLang = getNearestParentLangAttribute(element);
                if (mediaObjLang === langToUse) {
                    model = parseMedia(questionIdentifier, element, assessmentRunId);
                }
                if (!mediaObjLang && langToUse === defaultLanguage) {
                    model = parseMedia(questionIdentifier, element, assessmentRunId);
                }
            } else {
                model = parseMedia(questionIdentifier, element, assessmentRunId, mediaInfo);
            }

            if (model) {
                mediaModels.push(model);
            }
        }
    }
    return mediaModels;
}
